import { CalendarEventItem } from 'components/calendarEventItem'
import { CalendarPopover } from 'components/calendarPopover'
import { Typography } from 'components/typography'
import { addDays, startOfWeek } from 'date-fns'
import { TypographyTypes } from 'interfaces'
import dayjs from 'lib/dayjs'
import React, { useEffect, useMemo, useRef } from 'react'
import { CalendarEvent, formatLocalTime } from 'utils'
import { greedyFill } from 'utils/calendarSlots/calendarSlots'

export const CalendarWeek: React.FC<{
	currentMonth: Date
	days: Array<CalendarEvent>
}> = ({ currentMonth, days }): JSX.Element => {
	const daysShort = ['M', 'T', 'W', 'T', 'F', 'S', 'S']
	const maxQuantityElement = 4

	const weekStartsOn = 1
	const start = startOfWeek(currentMonth, { weekStartsOn })
	const dates = Array.from({ length: 7 }, (_, i) =>
		dayjs.utc(addDays(start, i)).format('D')
	)
	const hours = Array.from(
		{ length: 24 },
		(_, i) => `${i % 12 || 12}${i < 12 ? 'AM' : 'PM'}`
	)

	const daysFull = useMemo(
		() =>
			Array.from({ length: 7 }).map((_, index) =>
				dayjs.utc(addDays(start, index)).format('dddd')
			),
		[start]
	)
	const today = dayjs.utc().format('D')
	const currentHour = formatLocalTime(
		new Date().toISOString(),
		'h a'
	).toUpperCase()
	const containerRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		if (!containerRef.current) return

		const currentHourIndex = hours.findIndex(
			hour => hour.replace(/\s/g, '') === currentHour.replace(/\s/g, '')
		)

		if (currentHourIndex !== -1) {
			setTimeout(() => {
				const hourElements =
					containerRef.current?.querySelectorAll('div.sticky.left-0')
				const hourElement = hourElements?.[currentHourIndex]

				if (hourElement) {
					hourElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
				}
			}, 300)
		}
	}, [currentHour, hours])

	const getGridRowStart = (date: string): number => {
		return Number(formatLocalTime(date, 'HH')) + 2
	}

	const weekDates = useMemo(
		() => Array.from({ length: 7 }).map((_, i) => addDays(start, i)),
		[start]
	)

	const getGridColStart = (eventDate: string): number => {
		const convertDateUTC = formatLocalTime(eventDate, 'YYYY-MM-DD')
		const index = weekDates.findIndex(
			day =>
				dayjs.utc(day).format('YYYY-MM-DD') ===
				dayjs.utc(convertDateUTC).format('YYYY-MM-DD')
		)
		return index !== -1 ? index + 1 : 1
	}

	const eventsPerDay = useMemo(() => {
		const map = new Map<string, CalendarEvent[]>()

		days.forEach(event => {
			const dateKey = formatLocalTime(event.publish_date, 'yyyy-MM-dd')

			if (!map.has(dateKey)) {
				map.set(dateKey, [])
			}

			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			map.get(dateKey)!.push(event)
		})

		return map
	}, [days])

	const groupedEvents = useMemo(() => {
		const map = new Map<string, CalendarEvent[]>()

		days.forEach(event => {
			const eventDate = event.publish_date
			const key = `${formatLocalTime(eventDate, 'yyyy-MM-dd-HH')}`

			if (!map.has(key)) {
				map.set(key, [])
			}

			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			map.get(key)!.push(event)
		})

		return Array.from(map.entries())
	}, [days])

	return (
		<div
			ref={containerRef}
			className="isolate flex flex-auto flex-col overflow-auto bg-white h-full"
		>
			<div
				style={{ width: '165%' }}
				className="flex max-w-full flex-none flex-col sm:max-w-none md:max-w-full"
			>
				<div className="sticky top-0 z-30 flex-none bg-white shadow ring-1 ring-black/5 sm:pr-8">
					{/* Mobile View */}
					<div className="grid grid-cols-7 text-sm/6 text-gray-500 sm:hidden">
						{daysShort.map((day, index) => (
							<button
								key={index}
								type="button"
								className={`flex flex-col items-center pb-3 pt-2 ${
									Number(today) === Number(dates[index])
										? 'bg-blue-1 text-white'
										: 'br-white text-[#0A5159]'
								}`}
							>
								{day}
								<span className="mt-1 flex size-8 items-center justify-center font-semibold">
									{dates[index]}
								</span>
							</button>
						))}
					</div>

					{/* Desktop View */}
					<div className="-mr-px hidden grid-cols-7 divide-x divide-gray-100 border-r border-gray-100 text-sm/6 text-gray-500 sm:grid">
						<div className="col-end-1 w-14" />
						{daysFull.map((day, index) => {
							const fullDate = formatLocalTime(
								weekDates[index].toISOString(),
								'yyyy-MM-dd'
							)
							const eventCount = eventsPerDay.get(fullDate)?.length ?? 0

							return (
								<div
									key={index}
									className={`flex flex-col items-center justify-center py-3 ${
										Number(today) === Number(dates[index])
											? 'bg-blue-1 text-white'
											: 'br-white text-[#0A5159]'
									}`}
								>
									<span>
										{day}
										<span className="font-semibold ml-2">{dates[index]}</span>
									</span>
									<Typography
										title={`${eventCount} item${eventCount > 1 ? 's' : ''}`}
										type={TypographyTypes.h3}
										fontLeading="15/22"
										className={`${
											Number(today) === Number(dates[index])
												? 'text-white'
												: 'text-[#0A5159]'
										}`}
									/>
								</div>
							)
						})}
					</div>
				</div>
				<div className="flex flex-auto">
					<div className="sticky left-0 z-10 w-14 flex-none bg-white ring-1 ring-gray-100" />
					<div className="grid flex-auto grid-cols-1 grid-rows-1">
						{/* Horizontal lines */}
						<div
							className="col-start-1 col-end-2 row-start-1 grid divide-y divide-gray-100"
							style={{ gridTemplateRows: 'repeat(24, 160px)' }}
						>
							<div className="row-end-1 h-7 " />
							{hours.map((hour, index) => {
								const isCurrentHour = hour === currentHour.replaceAll(' ', '')

								return (
									<div
										key={index}
										className={isCurrentHour ? 'bg-[#E4FCFF]' : ''}
									>
										<div className="sticky left-0 z-20 -ml-14 -mt-2.5 w-14 pr-2 text-right text-xs/5 text-gray-400">
											{hour}
										</div>
									</div>
								)
							})}
						</div>
						{/* Vertical lines */}
						<div className="col-start-1 col-end-2 row-start-1 hidden grid-cols-7 grid-rows-1 divide-x divide-gray-100 sm:grid sm:grid-cols-7">
							<div className="col-start-1 row-span-full" />
							<div className="col-start-2 row-span-full" />
							<div className="col-start-3 row-span-full" />
							<div className="col-start-4 row-span-full" />
							<div className="col-start-5 row-span-full" />
							<div className="col-start-6 row-span-full" />
							<div className="col-start-7 row-span-full" />
							<div className="col-start-8 row-span-full w-8" />
						</div>
						{/* Events */}

						<ol
							className="col-start-1 col-end-2 row-start-1 grid grid-cols-1 sm:grid-cols-7 sm:pr-8"
							style={{
								gridTemplateRows: '1.75rem repeat(24, 160px) auto'
							}}
						>
							{groupedEvents.map(([key, events]) => {
								const eventDate = events[0].publish_date
								const rowStart = getGridRowStart(eventDate)
								const colStart = getGridColStart(eventDate)

								const { visibleEvents, hiddenEvents, visibleEffectiveSlots } =
									greedyFill(events, maxQuantityElement)

								const colStartClasses: Record<number, string> = {
									1: 'col-start-1',
									2: 'col-start-2',
									3: 'col-start-3',
									4: 'col-start-4',
									5: 'col-start-5',
									6: 'col-start-6',
									7: 'col-start-7'
								}

								return (
									<li
										key={key}
										className={`mt-px flex flex-col h-full gap-1 p-[6px] ${
											colStartClasses[colStart] ?? 'col-start-1'
										}`}
										style={{ gridRow: `${rowStart}` }}
									>
										{visibleEvents.map(event => (
											<div
												key={event.id}
												className={
													event.has_notification
														? 'flex-[2] min-h-0'
														: 'flex-1 min-h-0'
												}
											>
												<CalendarEventItem
													event={event}
													quantityElement={events.length}
													effectiveSlots={visibleEffectiveSlots}
												/>
											</div>
										))}

										{hiddenEvents.length > 0 && (
											<div className="p-1">
												<CalendarPopover
													day={hiddenEvents}
													maxQuantityElement={0}
													position="absolute"
													typeView="week"
												/>
											</div>
										)}
									</li>
								)
							})}
						</ol>
					</div>
				</div>
			</div>
		</div>
	)
}
